import { useEffect, useRef, useState } from 'react'
import { Link } from '@faststore/ui'
import classNames from 'classnames'
import DktLoader from 'src/components/DktLoader/DktLoader'
import { useCheckout } from 'src/sdk/checkout/useCheckout'
import { formatterPrice } from 'src/utils/formatterPrice'
import type { Variation } from 'src/components/product-page/Selector/types'
import { CashBackIcon } from 'src/components/Icons/CashbackIcon'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { getProductQueryObject } from 'src/utils/amplitude/useProductObject'

import type { ModalProductToAddProps } from '..'
import { SkuSelector } from '../../../sections/Sidebar/SkuSelector'
import { getInitialItem } from '../../../../utils/get-initial-sku'
import { MiniPdpImageCarousel } from './MiniPdpImageCarousel'
import { MiniPdpInfoAccordion } from './MiniPdpInfoAccordion'
import { MiniPdpSizeTable } from './MiniPdpSizeTable'
import { dispatchQuickViewEvent } from '../quickViewEvents'

export const MiniPdp = ({
  product,
  currentProduct,
  Images,
  addToCartFn,
  isLoading,
  style = 'add-item',
}: ModalProductToAddProps) => {
  const { pageData } = useGlobalContext()
  const standardObject = getStandardObject(pageData)
  const hasTrackedOpenRef = useRef<string | null>(null)
  const [variations, setVariations] = useState<Variation[]>([])
  const [currentSkuState, setCurrentSkuState] = useState(currentProduct)
  const [isShouldDisable, setIsShouldDisable] = useState(false)
  const [viewMode, setViewMode] = useState<'content' | 'size-guide'>('content')
  const contentScrollRef = useRef<HTMLDivElement>(null)
  const sizeGuideScrollRef = useRef<HTMLDivElement>(null)

  const initialSku =
    currentProduct ?? getInitialItem(product.items, product.linkText)

  const currentProductObject = getProductQueryObject(currentSkuState)

  const {
    orderForm: { items, messages },
    isLoading: checkoutLoading,
  } = useCheckout()

  const loading = isLoading ?? checkoutLoading
  const skuImages = currentSkuState.images?.length
    ? currentSkuState.images
    : Images

  const {
    sellerDefault: {
      sellerOffer: { cashPrice = 0, cashDiscountPercentage = 0 } = {},
    } = {},
  } = currentSkuState ?? {}

  const availableQuantity = currentSkuState.sellerDefault?.sellerOffer?.quantity
  const quantityAlreadyInCart = items?.find(
    (item) => item.id === currentSkuState.itemId
  )?.quantity

  const quantityNotAvailableMessage = messages?.find(
    (message) => message.code === 'itemQuantityNotAvailable'
  )

  const cannotIncreaseQuantity =
    quantityNotAvailableMessage?.fields?.ean === currentSkuState.ean

  useEffect(() => {
    if (hasTrackedOpenRef.current === currentProduct.itemId) {
      return
    }

    hasTrackedOpenRef.current = currentProduct.itemId

    dispatchQuickViewEvent('Quick View Opened', {
      standardObject,
      productObject: getProductQueryObject(currentProduct),
    })
  }, [currentProduct, standardObject])

  useEffect(() => {
    const shouldDisable =
      currentSkuState.isAvailable === false ||
      (quantityAlreadyInCart ?? 0) >= (availableQuantity ?? 0) ||
      !!cannotIncreaseQuantity

    setIsShouldDisable(shouldDisable)
  }, [
    availableQuantity,
    cannotIncreaseQuantity,
    currentSkuState,
    quantityAlreadyInCart,
  ])

  useEffect(() => {
    if (viewMode !== 'size-guide') {
      return
    }

    requestAnimationFrame(() => {
      sizeGuideScrollRef.current?.scrollTo({ top: 0 })
    })
  }, [viewMode])

  const handleOpenSizeGuide = () => {
    contentScrollRef.current?.scrollTo({ top: 0 })
    setViewMode('size-guide')
  }

  const buttonDisabledClasses =
    'cursor-not-allowed border-restructure-border-disabled bg-restructure-background-disabled text-restructure-disabled hover:border-restructure-border-disabled hover:text-restructure-disabled'

  const ctaText = style === 'change-item' ? 'Salvar' : 'Adicionar ao carrinho'

  const handleQuickViewAddToCart = async (
    type: 'addToCart' | 'buyNow',
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    await addToCartFn(type, currentSkuState ?? initialSku, event)

    dispatchQuickViewEvent('Quick View Added to Cart', {
      standardObject,
      productObject: currentProductObject,
    })
  }

  return (
    <div className="flex h-full flex-col">
      {viewMode === 'size-guide' ? (
        <div
          ref={sizeGuideScrollRef}
          className="min-h-0 flex-1 overflow-y-auto px-4 restructure-tablet:px-8"
        >
          <MiniPdpSizeTable
            product={product}
            onBack={() => setViewMode('content')}
          />
        </div>
      ) : (
        <>
          <div
            ref={contentScrollRef}
            data-mini-pdp-scroll
            className="min-h-0 flex-1 overflow-y-auto px-4 pb-8 restructure-tablet:px-8"
          >
            <MiniPdpImageCarousel
              images={skuImages}
              productName={product.productName}
            />

            <Link
              href={`/${product.linkText}/p`}
              className="mb-[27px] inline-flex items-center gap-2 "
            >
              <span className="text-lg leading-[24px] font-medium text-turboPickupLinkHover">
                Ir para a página do produto
              </span>
              <span className="text-turboPickupLinkHover text-lg">›</span>
            </Link>

            <div className="mb-6">
              <p className="text-base font-semibold text-restructure-primary">
                {product.brand}
              </p>

              <p className="mt-2 text-base font-semibold text-restructure-primary line-clamp-2">
                {product.productName}
              </p>
            </div>

            <SkuSelector
              classContainer="gap-5 restructure-tablet:pt-0 restructure-tablet:gap-8"
              productFromProps={product}
              setVariationsFromProps={setVariations}
              variationsFromProps={variations}
              currentSkuFromProps={currentProduct}
              setCurrentSkuFromProps={setCurrentSkuState}
              initialSkuFromProps={initialSku}
            />

            {product.sizeDetails && (
              <button
                type="button"
                className="mt-3 ml-auto block text-font-size-3 font-medium text-restructure-primary underline"
                onClick={handleOpenSizeGuide}
              >
                Tabela de medidas
              </button>
            )}

            <MiniPdpInfoAccordion
              product={product}
              currentSku={currentSkuState}
            />
          </div>

          {style === 'change-item' ? (
            <div className="mt-6 shrink-0">
              <button
                data-testid="cart-item-change-sku-save"
                className={classNames(
                  'w-full min-h-12 rounded-round px-xxs py-4 font-semibold text-base text-restructure-primary-inverted bg-restructure-background-action-1 hover:bg-restructure-background-action-1-hover restructure-tablet:min-h-16',
                  {
                    [buttonDisabledClasses]: isShouldDisable,
                  }
                )}
                onClick={(event) =>
                  handleQuickViewAddToCart('addToCart', event)
                }
                disabled={isShouldDisable}
              >
                {loading ? (
                  <div className="flex items-center justify-center w-full">
                    <DktLoader width={20} variant="black" />
                  </div>
                ) : (
                  <span>{ctaText}</span>
                )}
              </button>
            </div>
          ) : (
            <div className="sticky bottom-0 shrink-0 pt-3">
              <div className="gap-[6px] flex items-center justify-center bg-white py-4 text-center text-font-size-1 font-medium text-restructure-primary restructure-tablet:text-font-size-2">
                <CashBackIcon />
                5% de cashback para membro Clube Decathlon
              </div>
              <div className="w-full flex items-center gap-4 justify-between bg-turboPickupLinkHover px-6 py-4">
                <button
                  data-testid="modal-add-to-cart"
                  className={classNames(
                    'h-12 max-w-[198px] flex justify-center items-center rounded-round bg-white px-4 text-turboPickupLinkHover flex-1',
                    {
                      [buttonDisabledClasses]: isShouldDisable,
                    }
                  )}
                  onClick={(event) =>
                    handleQuickViewAddToCart('addToCart', event)
                  }
                  disabled={isShouldDisable}
                >
                  {loading ? (
                    <div className="flex items-center justify-center w-full">
                      <DktLoader width={20} variant="black" />
                    </div>
                  ) : (
                    <span className="text-base">{ctaText}</span>
                  )}
                </button>

                <span className="text-[20px] leading-[22px] font-semibold text-white whitespace-nowrap">
                  {formatterPrice(
                    (cashDiscountPercentage
                      ? cashPrice
                      : currentSkuState?.offer?.price) ?? 0
                  )}
                </span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
